import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "./ShopProducts.css";
import LoadingAnimation from "../components/LoadingAnimation";
import axiosInstance from "../api/axiosInstance";
import Swal from "sweetalert2";

const LOGIN_PATH = "/login";
const SIGNUP_PATH = "/signup-customer";

export default function PublicShopProducts() {
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState("");

  const requestIdRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const pageRef = useRef(1);
  const searchRef = useRef("");

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const fetchProducts = useCallback(async (pageNum, searchTerm, append = false) => {
    if (loadingRef.current && append) return;

    const requestId = ++requestIdRef.current;
    loadingRef.current = true;
    setLoadError("");

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLoadingMore(false);
    }

    const normalizedSearch = searchTerm
      .replace(/[,+]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    try {
      const response = await axiosInstance.get(
        `/medicine/list/?page=${pageNum}&search=${encodeURIComponent(normalizedSearch)}&medicine_category=branded`
      );

      if (requestId !== requestIdRef.current) return;

      const data = response.data;
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : [];

      setProducts((previous) => {
        if (!append) return items;

        const existingIds = new Set(previous.map((item) => item.id));
        return [...previous, ...items.filter((item) => !existingIds.has(item.id))];
      });

      const nextPageAvailable = Array.isArray(data)
        ? false
        : Boolean(data?.next);

      setHasMore(nextPageAvailable);
      hasMoreRef.current = nextPageAvailable;
      setPage(pageNum);
      pageRef.current = pageNum;
      setSuggestion(!append ? data?.suggestion || null : null);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Public medicine list failed:", error);
      setLoadError("Medicines could not be loaded. Please try again.");
      if (!append) setProducts([]);
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  // Load the first page and restart results when the search changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      pageRef.current = 1;
      setPage(1);
      setHasMore(false);
      hasMoreRef.current = false;
      fetchProducts(1, search, false);
    }, 350);

    return () => clearTimeout(timer);
  }, [search, fetchProducts]);

  // Search suggestions are public, read-only data.
  useEffect(() => {
    const term = search.trim();

    if (!term) {
      setSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      const normalizedSearch = term
        .replace(/[,+]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      try {
        const response = await axiosInstance.get(
          `/medicine/suggestions/?q=${encodeURIComponent(normalizedSearch)}`
        );

        if (!cancelled) {
          setSuggestions(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Medicine suggestions failed:", error);
          setSuggestions([]);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  // Infinite scroll: request the next page only after the current request finishes.
  useEffect(() => {
    const handleScroll = () => {
      if (
        loadingRef.current ||
        !hasMoreRef.current ||
        window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 120
      ) {
        return;
      }

      const nextPage = pageRef.current + 1;
      fetchProducts(nextPage, searchRef.current, true);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [fetchProducts]);

  const promptLogin = async () => {
    const result = await Swal.fire({
      title: "Login to continue",
      text: "Please log in or create an account to add medicines to your cart.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Login",
      cancelButtonText: "Sign Up",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#16a34a",
      reverseButtons: true,
    });

    const returnTo = `${location.pathname}${location.search}`;

    if (result.isConfirmed) {
      navigate(LOGIN_PATH, { state: { from: returnTo } });
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      navigate(SIGNUP_PATH, { state: { from: returnTo } });
    }
  };

  const handleAddToCart = (product) => {
    if (!product || Number(product.stock || 0) <= 0) return;
    // Public browsing only: do not call protected cart endpoints for guests.
    promptLogin();
  };

  const chooseSuggestion = (item) => {
    setSearch(item.name || "");
    setSuggestions([]);
    setShowSuggestions(false);
    setSuggestion(null);
  };

  if (loading && products.length === 0 && !loadError) {
    return <LoadingAnimation />;
  }

  return (
    <main className="shop-container">
      <section style={styles.searchWrapper}>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
          placeholder="Search branded medicines..."
          aria-label="Search branded medicines"
          autoComplete="off"
          style={styles.searchInput}
        />

        {showSuggestions && suggestions.length > 0 && (
          <div style={styles.suggestionsPanel}>
            {suggestions.map((item) => (
              <button
                type="button"
                key={item.id ?? item.name}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => chooseSuggestion(item)}
                style={styles.suggestionItem}
              >
                <span style={styles.suggestionName}>{item.name}</span>
                {item.salt_composition && (
                  <span style={styles.suggestionSalt}>{item.salt_composition}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {suggestion && search.toLowerCase() !== suggestion.toLowerCase() && (
          <p style={styles.didYouMean}>
            Did you mean:{" "}
            <button type="button" onClick={() => setSearch(suggestion)} style={styles.suggestionLink}>
              {suggestion}
            </button>
          </p>
        )}

        <p style={styles.searchHint}>
          Search by medicine name, salt name, or company name.
        </p>
      </section>

      {loadError && (
        <div role="alert" style={styles.errorBox}>
          <p>{loadError}</p>
          <button
            type="button"
            style={styles.retryButton}
            onClick={() => fetchProducts(1, search, false)}
          >
            Try again
          </button>
        </div>
      )}

      {!loadError && !loading && products.length === 0 && (
        <p style={styles.emptyState}>No branded medicines found. Try another search.</p>
      )}

      <section className="product-grid" aria-label="Branded medicines">
        {products.map((product) => {
          const price = Number(product.selling_price || product.price || 0);
          const mrp = Number(product.mrp || product.price || 0);
          const stock = Number(product.stock || 0);
          const discount = mrp > price && mrp > 0
            ? Math.round(((mrp - price) / mrp) * 100)
            : 0;

          const images = Array.isArray(product.images) && product.images.length
            ? product.images.filter(Boolean)
            : product.image
              ? [product.image]
              : [];

          return (
            <article key={product.id} style={styles.productCard}>
              <div style={styles.productInfo}>
                <h2 style={styles.productName}>{product.name}</h2>

                {product.salt_composition && (
                  <div style={styles.saltBadge}>🧪 {product.salt_composition}</div>
                )}

                {product.company && (
                  <div style={styles.company}>
                    by <strong>{product.company}</strong>
                  </div>
                )}
              </div>

              <div style={styles.imageFrame}>
                {images.length > 0 ? (
                  <PhotoProvider maskClosable>
                    <Swiper
                      spaceBetween={0}
                      slidesPerView={1}
                      pagination={{ clickable: true }}
                      modules={[Pagination]}
                      style={{ width: "100%", height: "100%" }}
                    >
                      {images.map((imageUrl, index) => (
                        <SwiperSlide key={`${product.id}-${index}`}>
                          <div style={styles.imageSlide}>
                            <PhotoView src={imageUrl}>
                              <img
                                src={imageUrl}
                                alt={product.name || "Medicine"}
                                loading="lazy"
                                onError={(event) => {
                                  if (!event.currentTarget.dataset.fallback) {
                                    event.currentTarget.dataset.fallback = "true";
                                    event.currentTarget.src = "/no-image.png";
                                  }
                                }}
                                style={styles.productImage}
                              />
                            </PhotoView>
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </PhotoProvider>
                ) : (
                  <div style={styles.noImage}>No image available</div>
                )}
              </div>

              <p style={styles.zoomHint}>Tap image to zoom</p>

              <div style={styles.priceBlock}>
                {mrp > price && <del style={styles.mrp}>₹{mrp.toFixed(2)}</del>}
                <div style={styles.price}>₹{price.toFixed(2)}</div>
                {discount > 0 && <div style={styles.discount}>{discount}% OFF</div>}
                {stock <= 0 && <div style={styles.outOfStock}>Out of Stock</div>}
              </div>

              <button
                type="button"
                onClick={() => handleAddToCart(product)}
                disabled={stock <= 0}
                style={{
                  ...styles.addButton,
                  ...(stock <= 0 ? styles.disabledButton : {}),
                }}
              >
                {stock <= 0 ? "Out of Stock" : "Add to Cart"}
              </button>
            </article>
          );
        })}
      </section>

      {loadingMore && (
        <div style={styles.loadingMore}>
          <LoadingAnimation small />
        </div>
      )}

      {!loading && !loadingMore && products.length > 0 && !hasMore && (
        <p style={styles.endMessage}>You have reached the end of the list.</p>
      )}
    </main>
  );
}

const styles = {
  searchWrapper: {
    maxWidth: 560,
    margin: "22px auto 26px",
    padding: "0 12px",
    position: "relative",
    boxSizing: "border-box",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 15px",
    borderRadius: 11,
    border: "1px solid #d1d5db",
    background: "#fff",
    fontSize: 14,
    outlineColor: "#2563eb",
  },
  suggestionsPanel: {
    position: "absolute",
    top: "calc(100% - 2px)",
    left: 12,
    right: 12,
    zIndex: 1000,
    maxHeight: "40vh",
    overflowY: "auto",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
  },
  suggestionItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 3,
    width: "100%",
    padding: "10px 12px",
    border: 0,
    borderBottom: "1px solid #f1f5f9",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
  },
  suggestionName: {
    color: "#1f2937",
    fontSize: 13,
    fontWeight: 650,
  },
  suggestionSalt: {
    color: "#64748b",
    fontSize: 11,
  },
  didYouMean: {
    margin: "9px 0 0",
    color: "#475569",
    fontSize: 13,
  },
  suggestionLink: {
    padding: 0,
    border: 0,
    background: "transparent",
    color: "#2563eb",
    fontWeight: 700,
    cursor: "pointer",
  },
  searchHint: {
    margin: "9px 0 0",
    color: "#64748b",
    textAlign: "center",
    fontSize: 12,
    lineHeight: 1.6,
  },
  errorBox: {
    maxWidth: 560,
    margin: "20px auto",
    padding: 16,
    borderRadius: 12,
    background: "#fff1f2",
    color: "#9f1239",
    textAlign: "center",
  },
  retryButton: {
    padding: "9px 15px",
    border: 0,
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  emptyState: {
    padding: 24,
    color: "#64748b",
    textAlign: "center",
  },
  productCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minWidth: 0,
    minHeight: 450,
    padding: 13,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
    boxSizing: "border-box",
    boxShadow: "0 3px 10px rgba(15,23,42,0.04)",
  },
  productInfo: {
    marginBottom: 8,
    textAlign: "center",
  },
  productName: {
    margin: "0 0 7px",
    color: "#1f2937",
    fontSize: 14,
    lineHeight: 1.4,
    fontWeight: 750,
    minHeight: 39,
  },
  saltBadge: {
    display: "inline-block",
    padding: "4px 7px",
    marginBottom: 5,
    borderRadius: 6,
    background: "#eef2ff",
    color: "#3730a3",
    fontSize: 11,
    lineHeight: 1.45,
  },
  company: {
    marginTop: 2,
    color: "#64748b",
    fontSize: 11,
  },
  imageFrame: {
    width: "100%",
    height: 175,
    overflow: "hidden",
    position: "relative",
    textAlign: "center",
  },
  imageSlide: {
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    cursor: "zoom-in",
  },
  noImage: {
    display: "flex",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    background: "#f8fafc",
    color: "#94a3b8",
    fontSize: 12,
  },
  zoomHint: {
    margin: "5px 0 9px",
    color: "#94a3b8",
    textAlign: "center",
    fontSize: 10,
  },
  priceBlock: {
    minHeight: 65,
    marginTop: "auto",
    paddingTop: 7,
    textAlign: "center",
  },
  mrp: {
    display: "block",
    color: "#dc2626",
    fontSize: 12,
  },
  price: {
    color: "#15803d",
    fontSize: 17,
    fontWeight: 700,
  },
  discount: {
    color: "#ea580c",
    fontSize: 12,
    fontWeight: 600,
  },
  outOfStock: {
    color: "#dc2626",
    fontSize: 12,
    fontWeight: 700,
  },
  addButton: {
    width: "100%",
    marginTop: 10,
    padding: "10px 8px",
    border: 0,
    borderRadius: 7,
    background: "#15803d",
    color: "#fff",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  disabledButton: {
    background: "#e5e7eb",
    color: "#64748b",
    cursor: "not-allowed",
  },
  loadingMore: {
    display: "flex",
    justifyContent: "center",
    padding: 18,
  },
  endMessage: {
    padding: 16,
    color: "#94a3b8",
    textAlign: "center",
    fontSize: 12,
  },
};
